<template>
  <div class="addPresetKitWrapper">
    <q-form @submit="onSubmit" class="form">
      <div class="form__item">
        <CustomInput
          label="Наименование пакета"
          :required="true"
          placeholder="Введите наименование пакета"
          :value="formData.preset_kit_name || ''"
          @change_input="value => onChange('preset_kit_name', value)"
        />
      </div>
      
      <div class="form__item">
        <div class="project-type-wrapper">
          <div v-if="!isCreatingNewProjectType" class="project-type-select-wrapper">
            <CustomSelect
              :options="projectTypesOptions"
              label="Тип проекта"
              option-value-key="project_type_id"
              option-label-key="project_type_name"
              :required="false"
              placeholder="Выберите тип проекта"
              :value="formData.project_type_id || null"
              @change="(value) => onChange('project_type_id', value)"
              :loading="isLoadingProjectTypes"
            />
          </div>
          
          <div class="create-project-type-toggle">
            <q-checkbox
              :model-value="isCreatingNewProjectType"
              @update:model-value="toggleCreateProjectType"
              label="Создать новый тип проекта"
            />
          </div>
        </div>
      </div>
      
      <div v-if="isCreatingNewProjectType" class="form__item">
        <CustomInput
          label="Код типа проекта"
          :required="true"
          placeholder="Например: PROJECT_DIRTY"
          :value="formData.project_type_code || ''"
          @change_input="value => onChange('project_type_code', value)"
          hint="Примеры: PROJECT_DIRTY, PROJECT_SCREED"
        />
      </div>
      
      <div class="form__item">
        <CustomSelect
          :options="presetTypesOptions"
          label="Город пакета"
          optionValueKey="preset_id"
          optionLabelKey="preset_name"
          :required="true"
          placeholder="Выберите город пакета"
          :value="formData.preset_id || ''"
          @change="(value, row) => onChange('preset_id', value, row)"
        />
      </div>

      <div class="form__item">
        <CustomSelect
          :options="sbsInagroKonstruktivOptions"
          label="Пакет в 1С"
          optionValueKey="sbs_inagro_konstruktiv_id"
          optionLabelKey="preset_kit_name_1c"
          :required="true"
          placeholder="Выберите пакет в 1С"
          :value="formData.sbs_inagro_konstruktiv_id || null"
          @change="(value, row) => onChange('sbs_inagro_konstruktiv_id', value, row)"
          class="sbs-select"
        />
      </div>

      <div class="form__item">
        <div class="select-with-button">
          <CustomSelect
            :options="placementTypesOptions"
            label="Тип помещения"
            optionValueKey="placement_type_id"
            optionLabelKey="placement_type_name"
            :required="true"
            :multiple="true"
            :value="formData.placement_type_ids || []"
            @change="(value) => onChange('placement_type_ids', value)"
            class="select-with-button__select placement-type-select"
          />
          <q-btn
            @click="openCreatePlacementTypeModal"
            class="select-with-button__btn"
            color="primary"
            icon="add"
            round
            size="sm"
            flat
          />
        </div>
      </div>

      <CustomModalFooter :btn-loading="loading" submit-btn-text="Создать пакет" />
    </q-form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue"
import { useQuery } from "@tanstack/vue-query"
import CustomInput from "@/components/form/CustomInput"
import CustomSelect from "@/components/form/CustomSelect"
import CustomModalFooter from "@/components/modal/modals/modal-footer"

import { useAppStore } from "@/services/store/app"
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar"
import {
  getPresetTypes,
  getSbsInagroKonstruktiv,
  createPresetKit,
  getAllPlacementTypes,
} from "@/views/PackageEntry/services"
import { getProjectTypes } from "@/views/BuildingSettings/services"

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  closeModal: {
    type: Function,
    default: () => {},
  },
});

const appStore = useAppStore();
const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();

const loading = ref(false);
const isCreatingNewProjectType = ref(false);
const isLoadingProjectTypes = ref(false);
const projectTypesOptions = ref([]);

const formData = reactive({
  preset_kit_id: null,
  preset_kit_name: "",
  project_type_id: null,
  project_type_code: "",
  preset_id: "",
  sbs_inagro_konstruktiv_id: "",
  placement_type_ids: [],
});

const { data: presetTypesData, refetch: refetchPresetTypes } = useQuery({
  queryKey: ["presetTypes"],
  queryFn: getPresetTypes,
  refetchOnWindowFocus: false,
  initialData: { data: [] },
});

const { data: sbsInagroKonstruktivData } = useQuery({
  queryKey: ["sbsInagroKonstruktiv"],
  queryFn: getSbsInagroKonstruktiv,
  refetchOnWindowFocus: false,
  initialData: { data: [] },
});

const { data: placementTypesData, refetch: refetchPlacementTypes } = useQuery({
  queryKey: ["allPlacementTypes"],
  queryFn: getAllPlacementTypes,
  refetchOnWindowFocus: false,
  initialData: { data: [] },
});

const presetTypesOptions = computed(() => presetTypesData.value?.data || []);

// Обрабатываем данные для поля "Пакет в 1С" - добавляем fallback если preset_kit_name_1c отсутствует
const sbsInagroKonstruktivOptions = computed(() => {
  const data = sbsInagroKonstruktivData.value?.data || [];
  return data.map(item => ({
    ...item,
    // Если preset_kit_name_1c отсутствует или пустое, используем sbs_inagro_konstruktiv_name как fallback
    preset_kit_name_1c: item.preset_kit_name_1c || item.sbs_inagro_konstruktiv_name || ''
  }));
});

const placementTypesOptions = computed(() => placementTypesData.value?.data || []);

const loadProjectTypes = async () => {
  isLoadingProjectTypes.value = true;
  try {
    const res = await getProjectTypes();
    projectTypesOptions.value = Array.isArray(res) ? res : (res?.data || []);
  } catch (e) {
    console.error('Error loading project types:', e);
    projectTypesOptions.value = [];
  } finally {
    isLoadingProjectTypes.value = false;
  }
};

const toggleCreateProjectType = (value) => {
  isCreatingNewProjectType.value = value;
  if (value) {
    // При включении галочки сбрасываем выбранный тип проекта
    formData.project_type_id = null;
    formData.project_type_code = '';
  } else {
    // При выключении очищаем поле кода
    formData.project_type_code = '';
  }
};

const onChange = (key, value, row) => {
  // CustomSelect всегда передает ID в value
  // Но на всякий случай проверяем и извлекаем ID, если value - это объект
  if (key === 'sbs_inagro_konstruktiv_id') {
    // Для поля "Пакет в 1С" всегда сохраняем только ID (число)
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      formData[key] = value.sbs_inagro_konstruktiv_id || null;
    } else {
      formData[key] = value || null;
    }
  } else if (key === 'preset_id') {
    // Для поля "Город пакета" также сохраняем только ID
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      formData[key] = value.preset_id || null;
    } else {
      formData[key] = value || null;
    }
  } else if (key === 'project_type_id') {
    // Для поля "Тип проекта" также сохраняем только ID
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      formData[key] = value.project_type_id || null;
    } else {
      formData[key] = value || null;
    }
  } else {
    // Для остальных полей сохраняем значение как есть
    formData[key] = value || null;
  }
};

const openCreatePlacementTypeModal = () => {
  appStore.showSecondaryModal("CreatePlacementType", {
    title: "Создать тип помещения",
    onClose: async (placementType) => {
      await refetchPlacementTypes();
      if (placementType?.placement_type_id) {
        const currentIds = formData.placement_type_ids || [];
        if (!currentIds.includes(placementType.placement_type_id)) {
          formData.placement_type_ids = [...currentIds, placementType.placement_type_id];
        }
      }
    },
  });
};

const onSubmit = async () => {
  // Базовая валидация
  if (!formData.preset_kit_name || !formData.preset_id || !formData.sbs_inagro_konstruktiv_id || !formData.placement_type_ids?.length) {
    showErrorSnackbar("Заполните все обязательные поля");
    return;
  }
  
  // Валидация типа проекта
  if (isCreatingNewProjectType.value) {
    // Если создаем новый тип проекта, проверяем код
    if (!formData.project_type_code) {
      showErrorSnackbar("Заполните код типа проекта");
      return;
    }
  } else {
    // Если используем существующий, проверяем что выбран
    if (!formData.project_type_id) {
      showErrorSnackbar("Выберите тип проекта");
      return;
    }
  }

  loading.value = true;
  
  // Убеждаемся, что sbs_inagro_konstruktiv_id - это только ID (число), а не объект
  let sbsInagroKonstruktivId = formData.sbs_inagro_konstruktiv_id;
  if (typeof sbsInagroKonstruktivId === 'object' && sbsInagroKonstruktivId !== null && !Array.isArray(sbsInagroKonstruktivId)) {
    sbsInagroKonstruktivId = sbsInagroKonstruktivId.sbs_inagro_konstruktiv_id || null;
  }
  
  // Убеждаемся, что preset_id - это только ID
  let presetId = formData.preset_id;
  if (typeof presetId === 'object' && presetId !== null && !Array.isArray(presetId)) {
    presetId = presetId.preset_id || null;
  }
  
  // Убеждаемся, что project_type_id - это только ID
  let projectTypeId = formData.project_type_id;
  if (typeof projectTypeId === 'object' && projectTypeId !== null && !Array.isArray(projectTypeId)) {
    projectTypeId = projectTypeId.project_type_id || null;
  }
  
  // API использует empty_to_none, поэтому null будет преобразован в None
  const body = {
    preset_kit_id: null,
    preset_kit_name: formData.preset_kit_name || null,
    preset_id: presetId,
    sbs_inagro_konstruktiv_id: sbsInagroKonstruktivId,
    placement_type_ids: formData.placement_type_ids || null,
    project_type_id: (!isCreatingNewProjectType.value && projectTypeId) ? projectTypeId : null,
    project_type_code: isCreatingNewProjectType.value ? formData.project_type_code || null : null,
    is_create_project_type: isCreatingNewProjectType.value,
  };

  const res = await createPresetKit(body);
  if (!res) {
    loading.value = false;
    return;
  }

  showSucceedSnackbar("Пакет успешно создан");

  if (props.data?.onSubmit) {
    props.data.onSubmit(res);
  }
  props.closeModal();
  loading.value = false;
};

onMounted(() => {
  loadProjectTypes();
});
</script>

<style scoped lang="scss">
.addPresetKitWrapper {
  max-width: 100%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
}
.form {
  display: flex;
  flex-direction: column;
  min-width: 100%;
  gap: 15px;
  margin-top: 15px;
}

.select-with-button {
  display: flex;
  align-items: center;
  gap: 8px;

  &__select {
    flex: 1;
  }

  &__btn {
    align-self: center;
    margin-top: 22px; // Выравнивание по центру input поля (учитывая label сверху)
  }
}

// Стили для поля "Тип помещения" - увеличенный шрифт
:deep(.placement-type-select) {
  .q-field__native span {
    font-size: 15px !important;
  }
  
  .q-field__label {
    font-size: 13px;
  }
}

// Стили для поля "Пакет в 1С" чтобы текст был виден
:deep(.sbs-select) {
  .q-field__native {
    color: #1d1d1d !important;
    
    span {
      color: #1d1d1d !important;
    }
  }
  
  .q-field__input {
    color: #1d1d1d !important;
  }
  
  .q-item__label {
    color: #1d1d1d !important;
  }
}

.project-type-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.project-type-select-wrapper {
  flex: 1;
}

.create-project-type-toggle {
  margin-top: -8px; // Небольшой отступ для выравнивания с полем
}
</style>